import { Group, Stack } from '@mantine/core';
import type {
	BarChartConfig,
	BiMetricConfig,
	ScatterChartConfig,
} from '@repo/api-codegen';
import { cloneDeep, keys } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { SelectableProperty } from '../../../components/EntityPageLayout/EntityPropertySidebar';
import { PropertySelector } from '../../../components/Properties/PropertySelector';
import { toTitleCase } from '../../../utils/charts/utils';
import { getAxesSettings } from './ChartTypeSelector.utils';

interface CategoryAxisSelectorProps {
	chartConfig: BiMetricConfig['chart_config'];
	onUpdate: (config: BiMetricConfig['chart_config']) => void;
}

const updateCategoryAxis = (
	config: BiMetricConfig['chart_config'],
	value: string | string[],
	multipleCategoryAxis: boolean
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (config.chartType === 'bar' || config.chartType === 'line') {
		const barAndLineConfig = newConfig as BarChartConfig;
		barAndLineConfig.axes.category = multipleCategoryAxis
			? (value as string[])
			: [value as string];
	}

	if (config.chartType === 'scatter') {
		const scatterConfig = newConfig as ScatterChartConfig;
		scatterConfig.axes.category = multipleCategoryAxis
			? (value as string[])
			: [value as string];
	}

	return newConfig;
};

export const CategorySection = observer(
	({ chartConfig, onUpdate }: CategoryAxisSelectorProps) => {
		// Category axis supports multiple values for bar, line, and scatter charts
		const multipleCategoryAxis =
			chartConfig.chartType === 'bar' ||
			chartConfig.chartType === 'line' ||
			chartConfig.chartType === 'scatter';

		const axesSettings = getAxesSettings(chartConfig);
		const categoryAxisColumns = axesSettings?.category || [];
		const columns = keys(chartConfig.columnConfigs);

		// Only show category axis selector for chart types that support it
		const supportsCategoryAxis =
			chartConfig.chartType === 'bar' ||
			chartConfig.chartType === 'line' ||
			chartConfig.chartType === 'scatter';

		if (!supportsCategoryAxis) {
			return null;
		}

		return (
			<Stack spacing="xs">
				<SelectableProperty label="Columns" dataTestId="category-columns">
					<Group w="100%" position="right">
						<PropertySelector
							selected={
								multipleCategoryAxis
									? categoryAxisColumns
									: (categoryAxisColumns[0] ?? '')
							}
							type={multipleCategoryAxis ? 'multi' : 'single'}
							onChange={(value) => {
								onUpdate(
									updateCategoryAxis(
										chartConfig,
										value as string | string[],
										multipleCategoryAxis
									)
								);
							}}
							value="columns"
							iconType="none"
							options={columns.map((column) => ({
								label: toTitleCase(column),
								value: column,
								icon: 'column',
							}))}
							isViewerUser={false}
						/>
					</Group>
				</SelectableProperty>
			</Stack>
		);
	}
);
